"use client";

import { useLocale } from "next-intl";
import { useCallback, useEffect, useRef, useState } from "react";

import { usePathname, useRouter } from "@/i18n/navigation";
import type { Locale } from "@/i18n/routing";

interface UseLocaleSelectorReturn {
  /** 현재 활성 locale */
  currentLocale: string;
  /** 드롭다운 열림 상태 */
  isOpen: boolean;
  /** 드롭다운 토글 */
  toggle: () => void;
  /** 드롭다운 닫기 */
  close: () => void;
  /** locale 전환 */
  switchLocale: (locale: Locale) => void;
  /** 드롭다운 컨테이너 ref (outside click 감지용) */
  containerRef: React.RefObject<HTMLDivElement | null>;
}

export function useLocaleSelector(): UseLocaleSelectorReturn {
  const currentLocale = useLocale();
  const router = useRouter();
  const pathname = usePathname();

  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);
  const close = useCallback(() => setIsOpen(false), []);

  const switchLocale = useCallback(
    (locale: Locale) => {
      router.replace(pathname, { locale });
      setIsOpen(false);
    },
    [router, pathname]
  );

  // Outside click 감지
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Escape 키 닫기
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen]);

  return {
    currentLocale,
    isOpen,
    toggle,
    close,
    switchLocale,
    containerRef,
  };
}
